import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const CHECK_INTERVAL_MS = 30 * 60_000;
const HIDDEN_RELOAD_MS = 5 * 60_000;

/** Whether the deployed bundle differs from the one this page is running. */
export async function isNewBuildDeployed(current: string = __BUILD_ID__, fetcher: typeof fetch = fetch): Promise<boolean> {
  try {
    const response = await fetcher("/version.json", { cache: "no-store" });
    if (!response.ok) return false;
    const { build } = await response.json() as { build?: unknown };
    return typeof build === "string" && build !== current;
  } catch { return false; }
}

/** Reloading here would drop something the user is in the middle of. */
export function reloadWouldInterrupt(pathname: string, doc: Document = document): boolean {
  return pathname.startsWith("/calls/") || doc.querySelector('[aria-modal="true"]') !== null;
}

/**
 * Keeps a long-open tab or desktop window on the latest deploy without asking.
 * Once a new build is out, the page reloads on the next screen change (it looks like the
 * navigation itself), or after sitting hidden for a while. Drafts live in sessionStorage,
 * so they survive the reload.
 */
export function useAutoRefresh(enabled: boolean = import.meta.env.PROD): void {
  const { pathname } = useLocation();
  const stale = useRef(false);
  const lastPath = useRef(pathname);
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  useEffect(() => {
    if (!enabled) return;
    let hiddenTimer: number | undefined;
    const check = async () => { if (!stale.current && await isNewBuildDeployed()) stale.current = true; };
    const onVisibility = () => {
      window.clearTimeout(hiddenTimer);
      if (document.visibilityState === "visible") { void check(); return; }
      hiddenTimer = window.setTimeout(() => {
        if (stale.current && document.visibilityState === "hidden" && !reloadWouldInterrupt(pathRef.current)) window.location.reload();
      }, HIDDEN_RELOAD_MS);
    };
    void check();
    const interval = window.setInterval(() => void check(), CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibility);
    return () => { window.clearInterval(interval); window.clearTimeout(hiddenTimer); document.removeEventListener("visibilitychange", onVisibility); };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || pathname === lastPath.current) return;
    lastPath.current = pathname;
    if (stale.current && !reloadWouldInterrupt(pathname)) window.location.reload();
  }, [enabled, pathname]);
}
